import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { customerApi, apiError, deviceId } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { LanguageSelector } from "@/components/LanguageSelector";
import { Turnstile } from "@/components/Turnstile";
import { ArrowLeft, Loader2, User, Phone, Languages, Building2, ChevronRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const LANGS = [
  { code: "en", label: "English" }, { code: "zh-CN", label: "简体中文" },
  { code: "zh-TW", label: "繁體中文" }, { code: "ko", label: "한국어" },
];

export default function Profile() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const { t, te, lang, setLang } = useLanguage();
  const [p, setP] = useState(null);
  const [name, setName] = useState("");
  const [language, setLanguage] = useState(lang);
  const [busy, setBusy] = useState(false);
  const [phoneStep, setPhoneStep] = useState(0);
  const [newPhone, setNewPhone] = useState("");
  const [code, setCode] = useState("");
  const [token, setToken] = useState("");

  useEffect(() => {
    customerApi.get("/profile").then(({ data }) => { setP(data); setName(data.name); setLanguage(data.language); });
  }, []);

  const saveProfile = async () => {
    setBusy(true);
    try {
      const { data } = await customerApi.patch("/profile", { name, language });
      setP((x) => ({ ...x, ...data }));
      setLang(language);
      await refresh();
      toast.success(t("saved"));
    } catch (e) { toast.error(te(apiError(e))); } finally { setBusy(false); }
  };

  const sendPhoneCode = async () => {
    if (!token) return toast.error(t("complete_security_check"));
    setBusy(true);
    try {
      await customerApi.post("/profile/phone/send-code", { phone: newPhone.trim(), turnstile_token: token, device_id: deviceId() });
      setPhoneStep(2); toast.success(t("toast_code_sent"));
    } catch (e) { toast.error(te(apiError(e))); } finally { setBusy(false); }
  };

  const verifyPhone = async () => {
    setBusy(true);
    try {
      const { data } = await customerApi.post("/profile/phone/verify", { phone: newPhone.trim(), code });
      setP((x) => ({ ...x, phone: data.phone })); setPhoneStep(0); setNewPhone(""); setCode(""); setToken("");
      toast.success(t("saved"));
    } catch (e) { toast.error(te(apiError(e))); } finally { setBusy(false); }
  };

  if (!p) return <div className="min-h-screen flex items-center justify-center text-primary">{t("loading")}</div>;

  return (
    <div className="min-h-screen bg-background">
      <LanguageSelector />
      <div className="max-w-md mx-auto px-6 py-8">
        <button data-testid="profile-back-btn" onClick={() => nav("/home")} className="flex items-center gap-2 text-primary font-medium mb-6">
          <ArrowLeft className="h-5 w-5" /> {t("back_to_home")}
        </button>
        <h2 className="font-display text-2xl font-bold text-primary">{t("my_profile")}</h2>

        <div className="mt-6 space-y-5">
          <div>
            <label className="flex items-center gap-2 text-sm font-semibold text-primary mb-2"><User className="h-4 w-4" /> {t("edit_name")}</label>
            <input data-testid="profile-name-input" value={name} onChange={(e) => setName(e.target.value)} className="input-lg" />
          </div>
          <div>
            <label className="flex items-center gap-2 text-sm font-semibold text-primary mb-2"><Languages className="h-4 w-4" /> {t("preferred_language")}</label>
            <select data-testid="profile-language-select" value={language} onChange={(e) => setLanguage(e.target.value)}
              className="w-full h-14 rounded-2xl border border-input bg-white px-4 text-base focus:ring-2 focus:ring-primary focus:outline-none">
              {LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
            </select>
          </div>
          <button data-testid="profile-save-btn" onClick={saveProfile} disabled={busy} className="btn-primary">
            {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : t("save")}
          </button>

          <div className="rounded-2xl border border-border bg-white p-4">
            <p className="flex items-center gap-2 text-sm font-semibold text-primary"><Phone className="h-4 w-4" /> {p.phone}</p>
            {phoneStep === 0 && (
              <button data-testid="profile-change-phone-btn" onClick={() => setPhoneStep(1)} className="mt-2 text-sm font-semibold text-primary underline">{t("change_phone")}</button>
            )}
            {phoneStep === 1 && (
              <div className="mt-3 space-y-3">
                <input data-testid="profile-newphone-input" value={newPhone} onChange={(e) => setNewPhone(e.target.value)} inputMode="tel" placeholder={t("new_phone")} className="input-lg" />
                <Turnstile onToken={setToken} />
                <div className="flex gap-2">
                  <button data-testid="profile-sendcode-btn" onClick={sendPhoneCode} disabled={busy || !token} className="flex-1 rounded-full bg-primary text-primary-foreground py-3 text-sm font-semibold disabled:opacity-60">{t("send_code")}</button>
                  <button onClick={() => { setPhoneStep(0); setToken(""); }} className="rounded-full border border-border px-4 text-sm">{t("cancel")}</button>
                </div>
              </div>
            )}
            {phoneStep === 2 && (
              <div className="mt-3 space-y-3">
                <div className="flex items-center gap-2 rounded-xl bg-secondary p-3 text-sm"><ShieldCheck className="h-5 w-5 text-primary" /> {t("code_sent_to", { phone: newPhone })}</div>
                <input data-testid="profile-code-input" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" placeholder={t("code_ph")} className="input-lg text-center tracking-[0.3em]" />
                <button data-testid="profile-verify-btn" onClick={verifyPhone} disabled={busy} className="w-full rounded-full bg-primary text-primary-foreground py-3 text-sm font-semibold">{t("verify_change")}</button>
              </div>
            )}
          </div>

          <button data-testid="profile-properties-btn" onClick={() => nav("/properties")}
            className="flex w-full items-center gap-3 rounded-2xl border border-border bg-white p-4 shadow-sm hover:border-primary/40 transition-colors">
            <Building2 className="h-5 w-5 text-primary" />
            <span className="flex-1 text-left font-semibold text-primary">{t("manage_properties")}</span>
            <ChevronRight className="h-5 w-5 text-primary/50" />
          </button>

          <div data-testid="profile-requests-info" className="rounded-2xl border border-border bg-white p-4 shadow-sm">
            <p className="font-semibold text-primary">{t("requests_info_title")}</p>
            <p className="mt-2 text-xs text-muted-foreground leading-relaxed">{t("enquiry_notice")}</p>
            <p className="mt-2 text-xs text-muted-foreground leading-relaxed">{t("legal_operator_note")}</p>
          </div>

          <p className="text-xs text-muted-foreground text-center">{t("member_since")}: {new Date(p.created_at).toLocaleDateString("en-NZ", { dateStyle: "medium" })}</p>
        </div>
      </div>
    </div>
  );
}
